import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { formatReferenceDateForApi } from "./isfSiteSetupService";

function milestoneRowHasColumns(row) {
  if (!row || typeof row !== "object") return false;
  const keys = new Set(
    Object.keys(row).map((key) => String(key).trim().toLowerCase()),
  );
  return keys.has("studymilestoneid") && keys.has("milestonecode");
}

function extractStudyMilestoneRows(response) {
  const data = response?.data?.data ?? response?.data ?? response;
  if (!data) return [];

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.StudyMilestones)) return data.StudyMilestones;

  if (Array.isArray(data?.Tables)) {
    const byColumns = data.Tables.find((table) => {
      const rows = table?.Rows || table?.rows || [];
      return rows.length > 0 && milestoneRowHasColumns(rows[0]);
    });
    if (byColumns) {
      return byColumns.Rows || byColumns.rows || [];
    }

    const table =
      data.Tables.find(
        (t) =>
          String(t?.TableName || t?.Name || "").toLowerCase() ===
          "studymilestones",
      ) || data.Tables[0];
    if (Array.isArray(table?.Rows)) return table.Rows;
    if (Array.isArray(table?.rows)) return table.rows;
  }

  if (data?.Tables && typeof data.Tables === "object") {
    for (const key of Object.keys(data.Tables)) {
      const rows = data.Tables[key];
      if (Array.isArray(rows) && rows.length && milestoneRowHasColumns(rows[0])) {
        return rows;
      }
    }
  }

  return [];
}

export function normalizeStudyMilestoneRow(row) {
  if (!row || typeof row !== "object") return null;
  return {
    studyMilestoneId: row.StudyMilestoneId || row.studyMilestoneId || "",
    milestoneId: row.MilestoneId || row.milestoneId || "",
    milestoneCode: row.MilestoneCode || row.milestoneCode || "",
    milestoneName: row.MilestoneName || row.milestoneName || "",
    gateCode: row.GateCode || row.gateCode || "",
    sortOrder: Number(row.SortOrder ?? row.sortOrder ?? 0),
    actualDate: row.ActualDate || row.actualDate || "",
    status: row.Status || row.status || "Pending",
    naReason: row.NaReason || row.naReason || "",
    anchorType: row.AnchorType || row.anchorType || "",
    anchorMilestoneCode:
      row.AnchorMilestoneCode || row.anchorMilestoneCode || "",
    anchorOffsetDays: row.AnchorOffsetDays ?? row.anchorOffsetDays ?? null,
    allowsNotApplicable:
      row.AllowsNotApplicable === true ||
      row.AllowsNotApplicable === 1 ||
      row.allowsNotApplicable === true,
    isInformational:
      row.IsInformational === true ||
      row.IsInformational === 1 ||
      row.isInformational === true,
    siteReferenceDate: row.SiteReferenceDate || row.siteReferenceDate || "",
  };
}

export function computeSuggestedMilestoneDate(row, milestoneByCode = {}) {
  if (!row) return "";
  const status = String(row.status || "Pending");
  if (status === "NotApplicable") return "";

  if (row.actualDate) {
    return formatReferenceDateForApi(row.actualDate) || row.actualDate;
  }

  const anchorType = String(row.anchorType || "").trim();
  const offset = Number(row.anchorOffsetDays ?? 0);

  if (anchorType === "ReferenceDate" && row.siteReferenceDate) {
    const base = new Date(row.siteReferenceDate);
    if (Number.isNaN(base.getTime())) return "";
    base.setDate(base.getDate() + (Number.isNaN(offset) ? 0 : offset));
    return formatReferenceDateForApi(base);
  }

  if (anchorType === "AnotherMilestone" && row.anchorMilestoneCode) {
    const parent = milestoneByCode[row.anchorMilestoneCode];
    if (!parent) return "";
    const parentDate =
      parent.actualDate ||
      computeSuggestedMilestoneDate(parent, milestoneByCode);
    if (!parentDate) return "";
    const base = new Date(parentDate);
    if (Number.isNaN(base.getTime())) return "";
    base.setDate(base.getDate() + (Number.isNaN(offset) ? 0 : offset));
    return formatReferenceDateForApi(base);
  }

  return "";
}

export function formatMilestoneAnchorHint(row, milestoneByCode = {}) {
  if (!row) return "";
  const anchorType = String(row.anchorType || "").trim();
  const offset = Number(row.anchorOffsetDays ?? 0);
  const offsetLabel =
    offset === 0
      ? ""
      : offset > 0
        ? ` +${offset}d`
        : ` ${offset}d`;

  if (anchorType === "ReferenceDate") {
    return `Reference date${offsetLabel}`;
  }
  if (anchorType === "AnotherMilestone" && row.anchorMilestoneCode) {
    const parent = milestoneByCode[row.anchorMilestoneCode];
    const parentDate = parent
      ? parent.actualDate ||
        computeSuggestedMilestoneDate(parent, milestoneByCode)
      : "";
    const chain = parentDate
      ? ` (after ${row.anchorMilestoneCode}: ${parentDate})`
      : ` (after ${row.anchorMilestoneCode})`;
    return `${row.anchorMilestoneCode}${offsetLabel}${chain}`;
  }
  return "";
}

export function isMilestoneDateEditable(row, milestoneByCode = {}) {
  if (!row || row.isInformational) return false;
  if (String(row.status) === "NotApplicable") return false;
  const anchorType = String(row.anchorType || "").trim();
  if (anchorType === "AnotherMilestone" && row.anchorMilestoneCode) {
    const parent = milestoneByCode[row.anchorMilestoneCode];
    if (!parent) return false;
    const parentReady =
      String(parent.status) === "Complete" ||
      String(parent.status) === "NotApplicable" ||
      Boolean(parent.actualDate);
    return parentReady;
  }
  return true;
}

export async function fetchStudyMilestones({
  organizationId,
  studyId,
  templateCategory,
  siteIdRef,
  createdBy,
  gateCode,
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.STUDY_MILESTONE, {
    ActionType: "GetStudyMilestones",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    SiteIdRef: siteIdRef || null,
    CreatedBy: createdBy || null,
    GateCode: gateCode || null,
  });
  return extractStudyMilestoneRows(response)
    .map(normalizeStudyMilestoneRow)
    .filter(Boolean)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.milestoneCode.localeCompare(b.milestoneCode));
}

export async function saveStudyMilestones({
  organizationId,
  studyId,
  templateCategory,
  siteIdRef,
  createdBy,
  milestones = [],
}) {
  return apiClient.post(API_ENDPOINTS.ISF.STUDY_MILESTONE, {
    ActionType: "SaveStudyMilestones",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    SiteIdRef: siteIdRef || null,
    CreatedBy: createdBy,
    Milestones: milestones.map((row) => ({
      MilestoneId: row.milestoneId,
      ActualDate: row.actualDate
        ? formatReferenceDateForApi(row.actualDate) || row.actualDate
        : null,
      Status: row.status || "Pending",
      NaReason: row.status === "NotApplicable" ? row.naReason || "" : null,
    })),
  });
}

export async function seedStudyMilestones({
  organizationId,
  studyId,
  templateCategory,
  siteIdRef,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.STUDY_MILESTONE, {
    ActionType: "SeedStudyMilestones",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    SiteIdRef: siteIdRef || null,
    CreatedBy: createdBy || null,
  });
}

export function buildMilestoneCodeMap(rows = []) {
  return (rows || []).reduce((acc, row) => {
    if (row?.milestoneCode) acc[row.milestoneCode] = row;
    return acc;
  }, {});
}

export function findStudyMilestoneForCode(rows = [], milestoneCode = "") {
  const code = String(milestoneCode || "").trim();
  if (!code) return null;
  return (rows || []).find((row) => row.milestoneCode === code) || null;
}
